import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import Button from '../../components/Button';
import { Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import OnboardingProgress from '../../components/OnboardingProgress';

const professions = [
  'Artist/Musician/Singer',
  'Songwriter',
  'Producer',
  'Instrumentalist',
  'Sound Engineer',
  'DJ/Performer',
  'Manager/Promoter',
  'Venue Representative',
  'Studio Owner/Manager',
  'Label Representative',
  'Other',
] as const;

export default function OnboardingProfession() {
  const navigate = useNavigate();
  const { user, updateProfile } = useAuthStore();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedProfessions, setSelectedProfessions] = React.useState<string[]>(
    user?.professions || []
  );
  const [otherProfession, setOtherProfession] = React.useState(user?.other_profession || '');

  const toggleProfession = (profession: string) => {
    setSelectedProfessions(prev => {
      const newProfessions = prev.includes(profession)
        ? prev.filter(p => p !== profession)
        : [...prev, profession];
      return newProfessions;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      console.log('Form submission started', { selectedProfessions, otherProfession });
      setIsSubmitting(true);
      setError(null);

      if (!selectedProfessions.length) {
        console.error('No professions selected');
        setError('Please select at least one profession');
        return;
      }

      if (selectedProfessions.includes('Other') && !otherProfession) {
        console.error('Other profession not specified');
        setError('Please specify your other profession');
        return;
      }

      console.log('Updating profile with:', {
        professions: selectedProfessions,
        other_profession: selectedProfessions.includes('Other') ? otherProfession : undefined,
      });

      await updateProfile({
        professions: selectedProfessions,
        other_profession: selectedProfessions.includes('Other') ? otherProfession : undefined,
      });

      console.log('Profile updated successfully');
      navigate('/onboarding/contact');
    } catch (error: any) {
      console.error('Failed to update profile:', error);
      setError(error.message || 'Failed to update profile. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-light px-4 py-8">
      <OnboardingProgress currentStep="profession" />
      <div className="max-w-md mx-auto">
        <h1 className="text-3xl font-bold font-dillan text-primary mb-2 text-center">
          What are your roles in the music world?
        </h1>
        <p className="text-neutral mb-6 text-center text-l">
          Select all roles that apply to you.
        </p>

        {error && (
          <p className="mb-6 p-4 rounded-full bg-secondary-dark bg-opacity-10 text-primary">
            {error}
          </p>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            {professions.map((profession) => {
              const isSelected = selectedProfessions.includes(profession);
              return (
                <button
                  key={profession}
                  type="button"
                  onClick={() => toggleProfession(profession)}
                  className={cn(
                    "mb-4 w-80 h-12 px-4 rounded-full border-4 border-neutral bg-surface-light text-neutral placeholder:text-neutral focus:border-neutral focus:ring-0 focus:outline-none font-dillan text-m mx-auto block flex items-center",
                    isSelected
                      ? "border-neutral bg-transparent bg-opacity-10 text-neutral"
                      : "border-neutral bg-surface-light text-neutral hover:border-secondary hover:text-neutral"
                  )}
                >
                  <div className={cn(
                    "w-5 h-5 rounded border-2 mr-3 flex items-center justify-center transition-colors",
                    isSelected
                      ? "border-accent bg-accent"
                      : "border-neutral"
                  )}>
                    {isSelected && <Check className="w-3 h-3 text-surface-light" />}
                  </div>
                  <span className="flex-1 text-left">{profession}</span>
                </button>
              );
            })}
          </div>

          {selectedProfessions.includes('Other') && (
            <div>
              <input
                type="text"
                value={otherProfession}
                onChange={(e) => setOtherProfession(e.target.value)}
                className="mb-4 w-80 h-12 px-4 rounded-full border-4 border-neutral bg-surface-light text-neutral placeholder:text-neutral focus:border-secondary focus:ring-0 focus:outline-none font-dillan text-m mx-auto block"
                placeholder="Please specify your other role(s)"
              />
            </div>
          )}


          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1 text-surface-light mt-4"
              onClick={() => navigate('/onboarding/name')}
              disabled={isSubmitting}
            >
              Back
            </Button>
            <Button 
              type="submit" 
              variant="primary" 
              className="flex-1 text-surface-light mt-4"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Continue'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}